import { PaginationConfig } from '@/components/features/data/DataTable'
import { Query, useQuery, useQueryClient } from '@tanstack/react-query'
import { PaginationState } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'

import { useFirstPageOnChange } from '@droidmap/frontend-ui'
import { OffsetPaged } from '@droidmap/shared-service-contract'

import { usePersistedPagination } from './usePersistedPagination'

export interface UseOffsetPaginationQueryOptions<
  T extends { id: string | number },
  TResponse extends OffsetPaged<T> = OffsetPaged<T>,
> {
  queryKey: readonly (string | number)[]
  queryFn: (options: { page: number; pageSize: number }) => Promise<TResponse>
  initialData?: TResponse
  /** Initial page size if no saved preference exists (defaults to 10). */
  initialPageSize?: number
  enabled?: boolean
  /** Time in ms before cached data is considered stale (defaults to 5 minutes). */
  staleTime?: number
  /** Optional key for persisting page size to localStorage. */
  preferenceKey?: string
  /**
   * Serialisable record of the current filter/sort state. When any field
   * changes the hook resets to page 0 and the queryKey changes so cached
   * pages from a previous filter/sort don't bleed into the new one.
   */
  resetKey?: Record<string, unknown>
  /** Polling interval in milliseconds. */
  refetchInterval?:
    | number
    | ((
        query: Query<TResponse, Error, TResponse, readonly (string | number)[]>,
      ) => number | false | undefined)
  refetchIntervalInBackground?: boolean
}

/**
 * Offset-based pagination hook for PG-backed list endpoints. Pairs with
 * services that return {@link OffsetPaged}. Pass filter/sort values into
 * `resetKey` so changing them snaps pagination back to page 0.
 */
export function useOffsetPaginationQuery<
  T extends { id: string | number },
  TResponse extends OffsetPaged<T> = OffsetPaged<T>,
>({
  queryKey,
  queryFn,
  initialData,
  initialPageSize = 10,
  enabled = true,
  staleTime = 5 * 60 * 1000,
  preferenceKey,
  resetKey,
  refetchInterval,
  refetchIntervalInBackground,
}: UseOffsetPaginationQueryOptions<T, TResponse>) {
  const queryClient = useQueryClient()
  const [paginationState, setPaginationState] = usePersistedPagination(
    preferenceKey,
    initialPageSize,
  )

  const resetKeyString = useMemo(
    () => (resetKey ? JSON.stringify(resetKey) : ''),
    [resetKey],
  )

  // Snap back to page 0 when filter/sort change. During the render, not in an
  // effect: the query below is keyed on the page as well as on `resetKey`, so
  // an effect lands one render too late and the list is read once for the new
  // filter on the old page — a page that may not exist under it — before the
  // reset takes and it is read again.
  useFirstPageOnChange(resetKeyString, setPaginationState)

  const shouldUseInitialData =
    initialData &&
    paginationState.pageSize === initialPageSize &&
    paginationState.pageIndex === 0 &&
    resetKeyString === ''

  const { data, isLoading, ...rest } = useQuery({
    queryKey: [
      ...queryKey,
      paginationState.pageIndex,
      paginationState.pageSize,
      resetKeyString,
    ],
    queryFn: () =>
      queryFn({
        page: paginationState.pageIndex,
        pageSize: paginationState.pageSize,
      }),
    initialData: shouldUseInitialData ? initialData : undefined,
    enabled,
    staleTime,
    placeholderData: (prev) => prev,
    refetchInterval,
    refetchIntervalInBackground,
  })

  const handlePaginationChange = useCallback(
    (
      updater: PaginationState | ((old: PaginationState) => PaginationState),
    ) => {
      setPaginationState((old) => {
        const next = typeof updater === 'function' ? updater(old) : updater
        // pageSize change → drop back to page 0
        if (next.pageSize !== old.pageSize) {
          return { ...next, pageIndex: 0 }
        }
        return next
      })
    },
    [setPaginationState],
  )

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey })
  }, [queryClient, queryKey])

  // Persist pageSize to localStorage when it changes (skip first render).
  const totalCount = data?.totalCount ?? 0
  const pageCount = data
    ? Math.max(1, Math.ceil(totalCount / paginationState.pageSize))
    : 0

  const paginationConfig = useMemo<PaginationConfig>(
    () => ({
      state: paginationState,
      onChange: handlePaginationChange,
      pageCount,
      totalCount,
      // Offset endpoints return an exact `totalCount`, so `pageCount` above is
      // the real number of pages and nothing exists beyond it — always `false`.
      // This used to be `pageIndex < pageCount - 1` ("not on the last page"),
      // which is what the flag means under *cursor* pagination: total unknown,
      // more may follow. That rendered an exact total as `N+` and hung a
      // trailing ellipsis after the final page number (droidmap#2410).
      hasMoreBeyondKnownPages: false,
    }),
    [paginationState, handlePaginationChange, pageCount, totalCount],
  )

  return {
    data: data?.items ?? [],
    totalCount,
    page: data?.page ?? paginationState.pageIndex,
    isLoading,
    pagination: paginationConfig,
    refresh,
    setPagination: handlePaginationChange,
    /**
     * Full server response, including any per-endpoint extra fields
     * (`statusCounts`, `typeCounts`, etc). Use this to read those without
     * a second query.
     */
    rawData: data,
    ...rest,
  }
}
